import 'react-toastify/dist/ReactToastify.css';
import 'react-contexify/ReactContexify.css';
import 'semantic-ui-css/semantic.min.css';
import './styles/tailwind.css';
import './scss/main.scss';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { store } from 'Models/Store';
import * as Models from 'Models/Entities';
import { reloadOnceForStaleChunk } from 'Util/staleChunkReload';
import { initObservability } from 'Util/_HumanWritten/Observability/observability';

// Faro/Sentry init — as early as possible so global error/vitals instrumentation
// covers app startup. No-ops unless window.observability (clientside-config) is set.
initObservability();

// @ts-expect-error
window.store = store;
// @ts-expect-error
window.Models = Models;

// Vite fires this when a dynamically-imported chunk fails to load — typically because a deploy
// replaced the content-hashed bundle under a still-open tab. Reload once (loop-guarded) to fetch
// the fresh index.html and its new chunk names. Covers dynamic imports that fail outside React's
// render path; failures inside a lazy route are additionally handled by the ErrorBoundary.
window.addEventListener('vite:preloadError', () => {
	// Deliberately NOT calling event.preventDefault(). Vite's preload helper is
	// `baseModule().catch(handlePreloadError)`, and handlePreloadError ends with
	// `if (!e.defaultPrevented) throw err` — so preventing the default makes that catch return
	// normally and the dynamic import RESOLVE WITH `undefined` instead of rejecting.
	//
	// React.lazy then stores that: `_status = 1, _result = undefined`, and the next render runs
	// `if (1 === a._status) return a._result.default` and dies with "Cannot read properties of
	// undefined (reading 'default')" (Safari: "undefined is not an object (evaluating
	// 'Oe._result.default')"). That message doesn't match isStaleChunkError, so the ErrorBoundary
	// misses the reload path and shows the error page on an ordinary navigation. GlitchTip
	// issues 396 and 400.
	//
	// Letting Vite rethrow gives React.lazy a real rejection, so the ErrorBoundary sees "Failed to
	// fetch dynamically imported module" and takes the reload path. The duplicate report this
	// handler was meant to suppress is already handled by reloadOnceForStaleChunk's reloadInFlight
	// flag, which exists for exactly that double-report.
	reloadOnceForStaleChunk();
});

const container = document.getElementById('root');
if (!container) {
	throw new Error('Failed to find the root element');
}

const root = createRoot(container);
root.render(
	<React.StrictMode>
		<App />
	</React.StrictMode>,
);
